/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Typography, Typography2 } from '@coursera/cds-core';

import type { Gradient } from 'bundles/xddp/utils/helpers';

type Props = {
  title: string;
  subtitle?: string;
  imageSrc: string;
  logoSrc?: string;
  gradient: Gradient;
  label?: string | JSX.Element;
};

const styles = {
  productCard: css`
    height: 384px;
    text-align: left;
    position: relative;
    width: 100%;
    transition: all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms;
    border-radius: 3px;
    overflow: hidden;
    box-shadow: rgb(31 31 31 / 12%) 0 1px 6px, rgb(31 31 31 / 12%) 0 1px 4px;
    cursor: pointer;
    color: rgb(255 255 255);
    background-color: rgb(31 31 31);
  `,
  cardImageContainer: css`
    box-align: center; /* stylelint-disable-line stylus/property-no-unknown */
    box-direction: normal; /* stylelint-disable-line stylus/property-no-unknown */
    box-orient: vertical; /* stylelint-disable-line stylus/property-no-unknown */
    height: 168px;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-color: rgb(189 189 189);
    color: rgb(255 255 255);
  `,
  cardImage: css`
    width: 100%;
    height: 100%;
    background-color: rgb(130 168 225);
    background-size: cover;
    background-position: center center;
  `,
  gradient: (gradient: Gradient) => css`
    width: 100%;
    height: 100%;
    position: absolute;
    top: 132px;
    background-image: ${'linear-gradient('
      .concat(gradient.deg.toString(), 'deg, ')
      .concat(gradient.start, ', ')
      .concat(gradient.end, ')')};
    transform: skewY(-10deg);
  `,
  partnerLogoContainer: css`
    width: 72px;
    height: 72px;
    padding: 8px;
    top: 96px;
    left: 24px;
    position: absolute;
    border: 1px solid rgb(225 225 225);
    border-radius: 3px;
    z-index: 1;
    background-color: rgb(255 255 255);
  `,
  partnerLogo: css`
    width: 100%;
    height: 100%;
    background-size: contain;
    background-position: center center;
    background-repeat: no-repeat;
  `,
  titleSection: css`
    position: relative;
    margin-top: 12px;
    z-index: 1;
    padding: 0.75rem 1.5rem;
    border-color: rgb(189 189 189);
    color: rgb(255 255 255);
  `,
  title: css`
    font-size: 1.4rem;
    line-height: 1.5rem;
    color: rgb(255 255 255);
    margin-bottom: 6px;
  `,
  subtitle: css`
    font-size: 1rem;
    line-height: 1.5rem;
    color: rgb(245 247 250);
  `,
  cardAction: css`
    width: 100%;
    position: absolute;
    bottom: 4px;
    padding: 0.75rem 1.5rem;
    border-color: rgb(189 189 189);
    color: rgb(255 255 255);
  `,
  label: css`
    font-size: 0.8rem;
    line-height: 1.125rem;
    text-transform: uppercase;
    font-weight: bold;
    color: rgb(245 247 250);
  `,
};

function ProductCard({ title, subtitle, imageSrc, logoSrc, gradient, label }: Props): React.ReactElement {
  return (
    <div css={styles.productCard} className="rc-ProductCard">
      <div css={styles.cardImageContainer}>
        <Imgix css={styles.cardImage} src={imageSrc} alt="card banner" />
      </div>
      <div css={styles.gradient(gradient)} />
      {!!logoSrc && (
        <span css={styles.partnerLogoContainer} className="rc-logoSrc">
          <Imgix css={styles.partnerLogo} src={logoSrc} alt="partner logo" />
        </span>
      )}
      <div css={styles.titleSection}>
        <Typography variant="h2" css={styles.title}>
          {title}
        </Typography>
        <Typography2 component="p" variant="bodySecondary" css={styles.subtitle}>
          {subtitle}
        </Typography2>
      </div>
      <div css={styles.cardAction}>
        <Typography2 component="p" variant="bodySecondary" css={styles.label}>
          {label}
        </Typography2>
      </div>
    </div>
  );
}

export default ProductCard;
